import React from "react";
import {
  EditAction,
  RECORD_FIELD_ROWS,
  RecordBlock,
  RecordField,
  UNSET,
} from "../../../components/ui";
import type { BillingDetails } from "../types/billing.types";

/**
 * The address as one line: the street, then where it is.
 *
 * Each part only if it is there, so details with a city and nothing else read
 * as a city rather than as a row of commas. The same line `brandAddress` draws
 * for the brand — written again rather than shared because the two take
 * different records, and widening that one to take either is how a brand's
 * address and a billing address would start being the same thing.
 */
const addressLine = (details: BillingDetails): string =>
  [details.address, details.city, details.state, details.pincode]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(", ");

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number — the same way the profile and the
 * brand read one back, and the same way the form draws it beside the field.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

interface BillingDetailsSummaryProps {
  details: BillingDetails;
  /** Opens the form, filled with these same answers. */
  onEdit: () => void;
  /** True while the purchase is with the server — Edit stands down with the
   *  form it would open. */
  busy?: boolean;
}

/**
 * Who the subscription is billed to, read back rather than asked again.
 *
 * This is what the checkout shows in place of `BillingDetailsForm` when
 * everything an invoice needs is already on the record — a practice that has
 * bought before, or one whose brand answers all eight fields. Buying a second
 * term should not open on a screenful of inputs somebody has to read through
 * to find out that nothing in it needs touching; it should say who the invoice
 * is going to, and offer the one control that matters if it is wrong.
 *
 * The same card, fields and blocks the profile and the brand are read back
 * with — see `RecordFields` — so a reader who checks their address on Settings
 * and their address here cannot tell the two screens apart. Both of the form's
 * sections fold into this one card: at rest these are not two questions being
 * asked, they are one answer being shown.
 *
 * The caption above the card is the form's own, at every width rather than the
 * phone-only one `RecordCard` carries. It stands where "Basic details" stands
 * over the form, and the column beside it is lined up against that caption —
 * see the `lg:mt-10` on the summary column in `BillingCheckoutPage`.
 *
 * It holds no state: whether the page is reading back or asking belongs to the
 * screen, which is also what has to submit.
 */
const BillingDetailsSummary: React.FC<BillingDetailsSummaryProps> = ({
  details,
  onEdit,
  busy = false,
}) => {
  const initial = details.fullName.trim().charAt(0).toUpperCase();

  return (
    <section>
      <h2 className="mb-2 flex items-end text-[13px] 3xl:text-[14px] text-ink-muted md:h-8">
        Basic details
      </h2>

      <div className="card-surface border border-line bg-white">
        {/* The lettered disc, the name, and the way back into the form at the
            far end — the header the client, the profile and the brand screens
            all open a record with.

            The row holds at every width rather than stacking into a phone's
            centred profile header: this card is one block of a checkout, not
            the subject of its own screen, and a 20px name over a full-width
            button would outweigh the plan being bought below it. Wrapping is
            left on, so a long name takes the line and the button drops under
            it rather than being squeezed. */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-6 sm:py-5">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#DEEBFB]"
          >
            <span className="text-[16px] font-medium text-[#2F6BAE]">
              {initial}
            </span>
          </span>
          <h3 className="min-w-0 text-[18px] sm:text-[20px] font-medium text-ink">
            {details.fullName}
          </h3>

          {/* A pencil on a phone, level with the name it edits, and the
              labelled button from `md` up — the same control a client's and an
              enquiry's header carry, off the same component. See `EditAction`,
              which keeps one accessible name at both widths.

              "Edit details" rather than a bare "Edit": the summary beside this
              card carries an Edit of its own that changes the plan, and two
              buttons a thumb apart reading the same word is how the wrong one
              gets pressed.

              Outlined where it is a button at all, which is what this app's
              read-only cards put their way back into editing in — and here it
              also keeps the teal off anything but the button that takes the
              money. The border is dropped on the phone so the circle is the
              same mark the other two screens draw. */}
          <EditAction
            label="Edit details"
            variant="outline"
            className="ml-auto max-md:self-start max-md:!border-0"
            disabled={busy}
            onClick={onEdit}
          />
        </div>

        {/* The three short answers about how to reach and bill this person,
            spread across the card from `md` up and a divided column of rows
            below it — see `RECORD_FIELD_ROWS`. */}
        <div
          className={`${RECORD_FIELD_ROWS} border-t border-line px-5 sm:px-6 md:gap-x-10`}
        >
          <RecordField
            label="Phone:"
            value={
              details.phone ? <DiallingCode>{details.phone}</DiallingCode> : UNSET
            }
          />
          <RecordField label="Email" value={details.email || UNSET} />
          {/* The one field an invoice can be raised without, so the one that
              can honestly read as unset here. */}
          <RecordField label="GSTIN" value={details.gstin || UNSET} />
        </div>

        {/* An address runs to a line and a half on a phone, and squeezed into
            the right of a row it would be set three words to a line. Its label
            goes above it instead, as on every other record screen. */}
        <div className="border-t border-line">
          <RecordBlock label="Address" value={addressLine(details) || UNSET} />
        </div>
      </div>
    </section>
  );
};

export default BillingDetailsSummary;
